import { DIMENSIONS } from './schema';
import type {
  Answers,
  CountryMatchResult,
  Dimension,
  ScoringWeights,
} from './types';

export const REASON_THRESHOLD = 0.55;
export const BLOCKER_THRESHOLD = 0.45;
export const COMPONENT_FLOOR = 0.05;

// ─── Household bucket (cost_of_living.json) ────────────────────────────────
//
// The five baskets cost_of_living.json carries — see
// docs/data/COST_OF_LIVING_SCHEMA.md for the exact composition of each.
export type HouseholdBucket = 'single' | 'couple' | 'family_1' | 'family_2' | 'family_3';

/**
 * Maps the quiz's `family` (solo/partner/kids-preschool/kids-school/pets) +
 * `family_children_count`
 * answers onto one of the five household baskets in cost_of_living.json.
 *
 * Mapping:
 *   - no kids, no partner (or unanswered)      → 'single'
 *   - no kids, partner selected                → 'couple'
 *   - kids present, children_count = '1'       → 'family_1'
 *   - kids present, children_count = '2'       → 'family_2'
 *   - kids present, children_count = '3plus'   → 'family_3'
 *   - kids present, children_count unanswered  → 'family_1' (see below)
 *
 * Lone-parent assumption (documented per task, not solved): the source data's
 * family_1/2/3 baskets assume 2 adults + N children (see schema doc). The
 * quiz has no way to distinguish "solo parent + 2 kids" from "couple + 2
 * kids" — both pick the same `kids-*` options, with or without `partner`. We
 * deliberately still bucket a lone parent into family_N rather than into
 * `single` or `couple`: groceries, childcare and schooling scale with the
 * number of children present, which tracks a lone parent's real spend far
 * better than a childless bucket would. The known distortion runs the other
 * way — family_N's rent assumes a 2-adult-sized dwelling, so a real lone
 * parent likely pays somewhat less than the figure we show them. There is no
 * lone-parent basket in the source data to do better than this; see
 * docs/data/COST_OF_LIVING_SCHEMA.md "Household definitions".
 *
 * Missing-count default: if kids are flagged but `family_children_count` was
 * never answered (e.g. a session persisted before this question existed),
 * default to `family_1` — the smallest family basket — rather than guessing
 * upward. Understating cost for a family_2/3 household in this rare fallback
 * case is preferable to overstating it for a family_1 household.
 */
export function deriveHouseholdBucket(answers: Answers): HouseholdBucket {
  const familyIds = answers.family?.optionIds ?? [];
  const hasKids = familyIds.includes('kids-preschool') || familyIds.includes('kids-school');
  if (hasKids) {
    const count = answers.family_children_count?.optionIds[0];
    if (count === '2') return 'family_2';
    if (count === '3plus') return 'family_3';
    return 'family_1'; // count === '1', or unanswered — see doc comment above
  }
  if (familyIds.includes('partner')) return 'couple';
  return 'single';
}

export type TFn = (key: string, opts?: Record<string, unknown>) => string;

export interface WeightedComponent {
  dim: Dimension;
  component: number;
  weight: number;
  contribution: number;
}

export function weightedComponents(
  components: Partial<Record<Dimension, number>>,
  weights: ScoringWeights,
): WeightedComponent[] {
  const out: WeightedComponent[] = [];
  for (const dim of DIMENSIONS) {
    const c = components[dim];
    const w = weights[dim] ?? 0;
    if (c == null || w <= 0) continue;
    out.push({ dim, component: c, weight: w, contribution: c * w });
  }
  return out;
}

export function topReasons(
  match: CountryMatchResult,
  weights: ScoringWeights,
  t: TFn,
  limit = 3,
): string[] {
  return topReasonsWithDim(match, weights, t, limit).map((r) => r.text);
}

export interface TopReason {
  dim: Dimension;
  text: string;
}

/**
 * Same as topReasons() but also returns the source dimension for each reason,
 * so callers can color a bullet or icon to match the dimension's palette.
 */
export function topReasonsWithDim(
  match: CountryMatchResult,
  weights: ScoringWeights,
  t: TFn,
  limit = 3,
): TopReason[] {
  const wc = weightedComponents(match.components, weights)
    .filter((x) => x.component >= REASON_THRESHOLD)
    .sort((a, b) => b.contribution - a.contribution)
    .slice(0, limit);
  return wc.map((x) => ({ dim: x.dim, text: t(`quiz.result.reason.${x.dim}`) }));
}

/**
 * Map a low-scoring dimension to a fragment derived from the user's own quiz
 * answers, e.g. for `language` if they picked "Russian only" we surface that
 * fact instead of the generic "Language barrier is high". Returns null when
 * we don't have a specific story to tell — caller falls back to generic text.
 */
function answerBasedDetail(
  dim: Dimension,
  answers: Answers,
  t: TFn,
): string | null {
  const detail = (token: string) => t(`quiz.result.blocker.detail.${token}`);
  switch (dim) {
    case 'language': {
      const langs = answers.languages?.optionIds ?? [];
      if (langs.includes('ru-only')) return detail('languageRuOnly');
      if (langs.length === 1 && langs[0] === 'en') return detail('languageEnOnly');
      return null;
    }
    case 'visa': {
      const passport = answers.passport?.optionIds[0];
      if (passport === 'cis') return detail('visaCis');
      if (passport === 'other') return detail('visaOther');
      return null;
    }
    case 'cost': {
      const bucket = answers.budget?.budgetBucket;
      if (bucket === 1 || bucket === 2) return detail('costLowBudget');
      return null;
    }
    case 'tax': {
      const incomes = answers.income?.optionIds ?? [];
      if (incomes.includes('remote') || incomes.includes('business')) return detail('taxRemote');
      return null;
    }
    case 'banking': {
      if (answers.passport?.optionIds[0] === 'cis') return detail('bankingCis');
      return null;
    }
    case 'healthcare': {
      if (answers.priorities?.optionIds.includes('healthcare')) return detail('healthcarePrio');
      return null;
    }
    case 'family': {
      const fam = answers.family?.optionIds ?? [];
      if (fam.includes('kids-preschool') || fam.includes('kids-school')) return detail('familyKids');
      return null;
    }
    case 'climate': {
      if ((answers.dealbreakers?.optionIds.length ?? 0) > 0) return detail('climateDealbreaker');
      return null;
    }
    default:
      return null;
  }
}

export function pickBlocker(
  match: CountryMatchResult,
  t: TFn,
  answers?: Answers | null,
): string | null {
  // Helper: take a generic blocker text and merge it with answer-derived
  // context if we have any. Falls back to the generic alone.
  const enrich = (dim: Dimension, generic: string): string => {
    const detail = answers ? answerBasedDetail(dim, answers, t) : null;
    return detail ? t('quiz.result.blocker.withContext', { generic, detail }) : generic;
  };

  if (match.blockers.length > 0) {
    const code = match.blockers[0];
    if (code.startsWith('low_')) {
      const dim = code.replace('low_', '') as Dimension;
      const key = `quiz.result.blocker.${dim}`;
      const translated = t(key);
      if (translated !== key) return enrich(dim, translated);
    }
    // Coded blockers (e.g. `banking_closed_for_ru`, `humidity_above_70`) carry
    // their own context inline — don't double-enrich them.
    const key = `quiz.result.blocker.${code}`;
    const translated = t(key);
    if (translated !== key) return translated;
  }
  let worst: WeightedComponent | null = null;
  for (const dim of DIMENSIONS) {
    const c = match.components[dim];
    if (c == null) continue;
    if (c < BLOCKER_THRESHOLD && (worst === null || c < worst.component)) {
      worst = { dim, component: c, weight: 1, contribution: c };
    }
  }
  if (worst) return enrich(worst.dim, t(`quiz.result.blocker.${worst.dim}`));
  return null;
}

// Approximate re-ranking using existing components × new weights. Mirrors the
// geometric weighted mean used in scoring.ts so live tweaks behave consistently.
export function rescoreWithWeights(
  match: CountryMatchResult,
  weights: ScoringWeights,
): CountryMatchResult {
  if (match.excluded) return match;
  let totalW = 0;
  for (const dim of DIMENSIONS) totalW += weights[dim] ?? 0;
  if (totalW <= 0) return { ...match, score: 0 };
  let logSum = 0;
  for (const dim of DIMENSIONS) {
    const w = weights[dim] ?? 0;
    if (w <= 0) continue;
    const c = Math.max(match.components[dim] ?? 0, COMPONENT_FLOOR);
    logSum += (w / totalW) * Math.log(c);
  }
  const base = Math.exp(logSum);
  const score = base * (1 - match.penalties.total);
  return { ...match, score: Math.max(0, Math.min(1, score)) };
}

// Quiet Atlas dimension palette — disciplined 6-tone subset of brand hues.
// Climate sits on terra (the strongest brand signal); language softens to
// ink-300; others fall between via lake / moss / ochre / rust.
export const DIM_COLORS: Record<Dimension, string> = {
  climate: 'bg-terra-600',
  cost: 'bg-moss-600',
  tax: 'bg-ochre-600',
  visa: 'bg-lake-600',
  healthcare: 'bg-rust-600',
  safety: 'bg-terra-300',
  language: 'bg-ink-300',
  jobs: 'bg-lake-200',
  family: 'bg-moss-200',
  banking: 'bg-ochre-200',
};

export const DIM_DOT_COLORS: Record<Dimension, string> = {
  climate: 'text-terra-600',
  cost: 'text-moss-600',
  tax: 'text-ochre-600',
  visa: 'text-lake-600',
  healthcare: 'text-rust-600',
  safety: 'text-terra-300',
  language: 'text-ink-300',
  jobs: 'text-lake-600',
  family: 'text-moss-600',
  banking: 'text-ochre-600',
};

export type ScoreBand = 'excellent' | 'strong' | 'fair' | 'weak';

export function scoreBand(score: number): ScoreBand {
  const pct = score * 100;
  if (pct >= 85) return 'excellent';
  if (pct >= 70) return 'strong';
  if (pct >= 55) return 'fair';
  return 'weak';
}
